import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import SensorBase from "../sensors/SensorBase";
import { t } from "../../../services/locale.service";
import { CORE_API } from "../../../constants";
import { getData } from "../../../services/common.service";
import { DataTable } from "react-library";
import { useRecoilValue } from "recoil";
import { selectedPlotState } from "../../../state/atoms/global.atom";
import { TDiseasePestData } from "../../plotChart/types";

interface applicableTo {
  cropId: string;
  diseasePestId: string;
  phi: number;
}
interface Item {
  applicableTo: applicableTo[];
  displayName?: string;
  name?: string;
  id: string;
}
interface ResultItem {
  element: string;
  phi: number | undefined;
}

interface WhenToSpray {
  days: number;
  severity: string;
}
interface Item {
  displayName?: string;
  config?: {
    whenToSpray?: WhenToSpray[];
  };
  stageId?: string;
}

const Diseases: React.FC = () => {
  const { sensor } = useParams();
  const { state } = useLocation();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const selectedPlot = useRecoilValue(selectedPlotState);

  const TabButtons = {
    week: t("1_Week"),
    month: t("1_Month"),
    year: t("1_Year"),
  };

  // yeah decide krayga ki isDisease k liye hai ki pest k liye
  const diseasePest = state.isDisease
    ? "Disease"
    : state.isPest
      ? "Pest"
      : undefined;

  const [tableDataForChem, setTableDataForChem] = useState<string[][]>([]);
  const [tableDataForSprayAlerts,setTableDataForSprayAlerts]=useState<(string | number)[][]>([[""]]);


  function joinString(whenToSpray: Array<{ days: number; severity: string }>): string {
    const strArr = whenToSpray.map((item) =>
      t("whenToSprayDescription").replace("{{severity}}", t(item.severity)).replace("{{days}}", String(item.days)));
    return strArr.join(`\n ${t("or")} \n`);
  }

  const getCropId = async (oldId: string): Promise<string> => {
    try {
      const filterQuery = {
        where: {
          type: "crop",
          oldId: oldId,
        },
      };

      const url = `/id-mappers?filter=${JSON.stringify(filterQuery)}`;

      const response = await getData(url, {
        sendTokenInHeader: true,
        basePath: CORE_API,
      });

      if (!response || response.length === 0) {
        throw new Error("No crop ID found");
      }

      return response[0].newId;  
    } catch (error) {
      console.error("Error fetching crop ID:", error);
      throw error;
    }
  };

  const fetchFertilizers = async (
    cropId: string,
    diseasePestId: string,
    lang: string = "hi"
  ): Promise<{ element: string; phi: number | undefined }[]> => {
    try {
      const filterQuery = {
        where: {
          applicableTo: {
            elemMatch: {
              cropId: cropId,
              diseasePestId: diseasePestId,
            },
          },
        },
      };

      const url = `/fertilizers-list?filter=${JSON.stringify(filterQuery)}`;

      const response = await getData(url, {
        sendTokenInHeader: true,
        basePath: CORE_API,
        headers: {
          lang: lang,
        },
      });

      if (!response || response.length === 0) {
        console.warn("No fertilizers found");
        return [];
      }

      const result: ResultItem[] = response.map((item: Item) => {
        const phi = item?.applicableTo?.find(
          (applicable: applicableTo) =>
            applicable.cropId === cropId &&
            applicable.diseasePestId === diseasePestId
        )?.phi;

        return {
          element: item.displayName || item.name || item.id,
          phi: phi,
        };
      });
      // Sort by PHI ascending
      return result.sort((a, b) => (a.phi ?? 0) - (b.phi ?? 0));
    } catch (error) {
      console.error("Error fetching fertilizers:", error);
      throw error;
    }
  };

  const getStageWiseAlerts = async (
    cropId: string,
    diseasePestId: string,
    varietyId: string,
    lang: string = "hi"
  ): Promise<Array<Array<string | number>>> => {
    try {
      const url = `/spray-configs/${cropId}/${diseasePestId}?cropVarietyId=${varietyId}`;

      const response = await getData(url, {
        sendTokenInHeader: true,
        basePath: CORE_API,
        headers: {
          lang: lang,
        },
      });

      if (!response || response.length === 0) {
        console.warn("No stage-wise alerts found");
        return [];
      }

      const transformedData = response.map((item: Item) => [
        // Crop stage name
        item.displayName || "Unknown Stage",
      
        // Spray guidelines or "Not Applicable" if config or whenToSpray is unavailable
        item.config?.whenToSpray && item.config.whenToSpray.length > 0
          ? joinString(item.config.whenToSpray)
          : t("notApplicable"),
      ]);

      return transformedData;
    } catch (error) {
      console.error("Error fetching stage-wise alerts:", error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
  
      try {
        if (!selectedPlot || !(selectedPlot.cropSown as { cropId?: string })?.cropId) {
          console.warn("selectedPlot or cropId is not available.");
          setLoading(false);
          return;
        }

        // Step 1: Get crop ID
        const fetchedCropId = await getCropId(`${(selectedPlot.cropSown as { cropId?: string }).cropId}`);
        
        if (!fetchedCropId) {
          console.warn("Crop ID not found.");
          setLoading(false);
          return;
        }
  
        // Step 2: Fetch fertilizers using the crop ID
        const fertilizers = await fetchFertilizers(fetchedCropId, state.diseasePestid);
        const tableData = fertilizers.map((item) => [
          item?.element || "",
          item?.phi !== undefined ? item.phi.toString() : "",
        ]);
        setTableDataForChem(tableData);
  
        // Step 3: Get Stage-Wise Alerts
        const stagewiseAlerts = await getStageWiseAlerts(
          fetchedCropId,
          state.diseasePestid,
          `${(selectedPlot.cropSown as { varietyId?: string }).varietyId}`
        );
        setTableDataForSprayAlerts(stagewiseAlerts);
      } catch (err) {
        if (err instanceof Error) {
          console.error("Error:", err.message);  
          setError(err.message);
        } else {
          console.error("An unexpected error occurred:", err);  
          setError("An unexpected error occurred.");
        }
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [selectedPlot]);
  

  // Handle loading and error states
  if (loading) return <div>{t("Loading")}</div>;
  if (error) return <div>Error: {error}</div>;

 
  const getRiskColor = (riskLevel: string): string => {
    switch (riskLevel) {
    case "LOW":
      return "#27AE60"; // Green
    case "MEDIUM":
      return "#F4D03F"; // Yellow
    case "HIGH":
      return "#C0392B"; // Red
    default:
      return "#27AE60"; // Green as default
    }
  };

  const customProcessForDiseasePest = (data: TDiseasePestData) => {
    const color = getRiskColor(data.riskLevel);  
    return {
      ...data,
      color,  
    };
  };

  return (
    
    <div>
      <SensorBase
        field={sensor!}
        unit="%"
        tabButtons={TabButtons}
        diseasePest={diseasePest}
        customProcessForDiseasePest={customProcessForDiseasePest}
      />
      {tableDataForChem && tableDataForChem.length>0 && (
        <div style={{ padding: "12px", margin: "0 auto" }}>
          <h3 style={{ textAlign: "center", fontSize: "14px" }}>
            {t("useChemicals")}
          </h3>
          <DataTable
            columns={[t("element"), t("PHItitle")]}
            data={tableDataForChem}
            columnWidths={[80, 80]}
          />
        </div>
      )}

      {tableDataForSprayAlerts && tableDataForSprayAlerts.length>0 && (

        <div style={{ padding:"12px", margin: "0 auto" }}>
          <h3 style={{textAlign:"center",fontSize:"14px"}}>{t("StagewiseSprayGuideline")}</h3>
          <DataTable
            columns={[t("CropStages"), t("whenToSpray")]}
            data={tableDataForSprayAlerts}
            columnWidths={[80, 80]}
          />
        </div>
      )}
    </div>
  );
};

export default Diseases;
